<template>
  <div :id="sectionComponentId" class="module-plan-section-component">
    <AccordionWrapper
      class="module-plan-accordion-component section"
      @state-change="toggleSectionAccordion"
    >
      <cv-accordion-item :open="isAccordionOpen">
        <template #title>
          <match-indicator v-if="checkSectionIdMatched(section.id)" />
          <div class="wrapper">
            <div class="component-content">
              <div v-if="showSelectButtonForSection" class="all-checkbox" @click.stop>
                <input-checkbox
                  :id="`section-list-${section.id.toString()}`"
                  :value="section.id.toString()"
                  :selected-values="selectedSections"
                  :disabled="isCheckBoxDisabledForSection"
                  @change="handleCheckboxChange(section.id, $event)"
                />
              </div>
              <div v-if="showRadioButtonForSection" class="all-checkbox" @click.stop>
                <input-radio
                  :id="section.id.toString()"
                  :value="section.id.toString()"
                  :selected-value="selectedSection"
                  :disabled="isRadioButtonDisabledForSection"
                  @change="handleRadioChange"
                  @click.stop
                />
              </div>
              <dl class="field-set accordion-title title">
                <dd class="field-value">
                  <span
                    class="icon"
                    :class="{
                      'hide-section-activity-number': hideSectionActivityNumber,
                    }"
                  >
                    <div
                      class="section-label"
                      :data-section-icon-text="
                        sectionIconText(showSectionCover ? section.pageNo : null)
                      "
                    >
                      <svg-icon :name="sectionIconType" kind="indicative" />
                    </div>
                  </span>
                  <span class="accordion-text">{{ section.title }}</span>
                </dd>
              </dl>
              <cv-form v-if="sectionSettings && showSectionCover" class="schedule">
                <dl class="field-set">
                  <dd class="field-value">
                    <div class="start">
                      <div class="date">
                        {{ dateFormatter(sectionSettings?.startDateTime) }}
                      </div>
                      <div class="time">
                        {{ timeFormatter(sectionSettings?.startDateTime) }}
                      </div>
                    </div>
                    <svg-icon name="CircleArrowRight32" kind="indicative" />
                    <div class="end">
                      <div class="date">
                        {{ dateFormatter(sectionSettings?.endDateTime) }}
                      </div>
                      <div class="time">
                        {{ timeFormatter(sectionSettings?.endDateTime) }}
                      </div>
                    </div>
                  </dd>
                </dl>
              </cv-form>
              <dl v-if="sectionStatusInfo && isAssignmentLesson" class="field-set status">
                <dd class="field-value">
                  <label-component
                    class="section-status"
                    :kind="sectionStatusKind"
                    :text="sectionStatusText"
                  />
                </dd>
              </dl>
            </div>
            <div v-if="isTeacher && isAssignmentLesson && showSectionCover" class="action-buttons">
              <cv-button class="icon-button monitor" kind="ghost" @click="handleMonitor">
                <cv-tooltip
                  direction="bottom"
                  alignment="center"
                  :tip="t('module_plan_monitoring_button_tooltip_text')"
                >
                  <svg-icon name="Monitor24" kind="action" />
                </cv-tooltip>
              </cv-button>
            </div>
          </div>
        </template>

        <template #content>
          <template v-if="isBlank">
            <empty-placeholder>
              <template #icon>
                <AnnotationCardsEmpty />
              </template>
              {{ t("module_plan_no_notes_and_description") }}
            </empty-placeholder>
          </template>
          <template v-else>
            <div class="wrapper" @click.stop>
              <template v-if="isTeacher">
                <h6>{{ t("module_plan_notes") }}</h6>
                <rich-text
                  :lazyload="false"
                  :content="sectionNotes"
                  :expandable="true"
                  :expanded="true"
                />
              </template>
              <h6>{{ t("module_plan_description") }}</h6>
              <rich-text
                v-if="isAccordionOpen"
                :lazyload="false"
                :content="sectionDescription"
                :expandable="true"
                :expanded="true"
              />
            </div>
          </template>
        </template>
      </cv-accordion-item>
    </AccordionWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import StringUtils from "@utils/string.utils.ts";
import { LESSON_ACTIVITY_INDICATOR_ICON } from "@modules/editor/service/editor.constant.ts";
import { FAILED_REASON } from "@plugins/rule-engine/rule.constant.ts";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
import DateTimeUtils from "@utils/datetime.utils.ts";
import { NAMESPACE, RESOURCE_DOMAIN } from "@modules/search/service/search.constant.ts";
import { ROUTES_VIEW } from "@modules/attempt/service/attempt.constant.ts";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant";
import AuthoringCopilotTemplateSelectionMixin from "@modules/editor/mixin/authoring.copilot.template.selection.mixin";
import AnnotationCardsEmpty from "@images/placeholder/empty-states/AnnotationCardsEmpty.svg?component";
import AccordionWrapper from "@plugins/common/components/AccordionWrapper.vue";
import MatchIndicator from "@plugins/common/components/MatchIndicator.vue";
import InputCheckbox from "@plugins/common/components/InputCheckbox.vue";
import InputRadio from "@plugins/common/components/InputRadio.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import LabelComponent from "@plugins/common/components/LabelComponent.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import { useStore } from "vuex";
import { useVm } from "@utils/vm.ts";
import {
  LessonSelectionModeEnum,
  LessonSelectionTypeEnum,
} from "@modules/resource-search/resource-search-types.ts";

defineOptions({
  name: "ModulePlanSectionComponent",
  mixins: [AuthoringCopilotTemplateSelectionMixin],
});

interface Props {
  module: Record<string, unknown>;
  section: Record<string, unknown>;
  selectedSections?: string[];
  isAccordionOpen: boolean;
  hideSectionActivityNumber: boolean;
  params?: Record<string, unknown>;
  selectionMode?: string;
}

const props = withDefaults(defineProps<Props>(), {
  selectedSections: () => [],
  params: () => ({}),
});

const emit = defineEmits<{
  (e: "toggleSectionAccordion", value: { sectionId: string; expanded: boolean }): void;
  (e: "updateSelectedSection", sectionId: string, isChecked?: boolean): void;
}>();

const { t } = useI18n();
const router = useRouter();
const vuexStore = useStore();
const { vm } = useVm();

const isTeacher = computed(() => vuexStore.getters[`${NAMESPACE.AUTH}/isTeacher`]);

const sectionComponentId = computed(() => `section-${props.section?.id}`);

const selectedSection = computed(() => {
  return props.selectedSections.includes(String(props.section.id))
    ? String(props.section.id)
    : undefined;
});

const sectionAccess = computed(() => props.section.access);

const sectionStatusInfo = computed(() => props.section.statusInfo);

const sectionSettings = computed(() => props.section.settings);

const sectionNotes = computed(() => props.section.notes || t("module_plan_no_notes"));

const sectionDescription = computed(
  () => props.section.description || t("module_plan_no_description"),
);

const sectionIconText = (sectionPageNo: number | null) => {
  return sectionPageNo ? `${StringUtils.getAlphabeticalLetter(sectionPageNo - 1)}` : "";
};

const sectionIconType = computed(() => {
  const ICON = LESSON_ACTIVITY_INDICATOR_ICON.SECTION;

  if (!showSectionCover.value) return ICON.HIDDEN;
  if (isRestricted.value) return ICON.RESTRICTED;
  if (sectionStatusInfo.value?.completed) return ICON.COMPLETED;

  return ICON.VIEWABLE;
});

const sectionStatusKind = computed(() => {
  if (isRestricted.value) return "restricted";
  if (sectionStatusInfo.value?.completed) return "complete";
  if (isOverdue.value) return "overdue";
  return "incomplete";
});

const sectionStatusText = computed(() => {
  if (isRestricted.value) {
    return t("restricted_label_uppercase");
  }
  if (sectionStatusInfo.value.completed) {
    return t("complete_label_uppercase");
  }
  if (isOverdue.value) {
    return t("overdue_label_uppercase");
  }
  return t("incomplete_label_uppercase");
});

const showSectionCover = computed(() => props.module.showSectionCover);

const isFromAuthoringCopilotViewDetails = computed(() => vm.isFromAuthoringCopilotViewDetails);

const isCheckBoxDisabledForSection = computed(() => {
  if (!showSectionCover.value) return true;
  if (isFromAuthoringCopilotViewDetails.value) return true;
  return props.selectionMode === LESSON_ACTIVITY_TYPE.ACTIVITY;
});

const isRadioButtonDisabledForSection = computed(() => {
  return isFromAuthoringCopilotViewDetails.value;
});

const showSelectButtonForSection = computed(() => {
  return (
    props.params?.selectionMode === LessonSelectionModeEnum.multiple &&
    (props.params.selectionType === LessonSelectionTypeEnum.activityOrSection ||
      props.params.selectionType === LessonSelectionTypeEnum.sectionOrLesson) &&
    showSectionCover.value
  );
});

const showRadioButtonForSection = computed(() => {
  return (
    props.params?.selectionMode === LessonSelectionModeEnum.single &&
    (props.params.selectionType === LessonSelectionTypeEnum.activityOrSection ||
      props.params.selectionType === LessonSelectionTypeEnum.sectionOrLesson) &&
    showSectionCover.value
  );
});

const isAssignmentLesson = computed(
  () =>
    props.module.resourceDomain === RESOURCE_DOMAIN.ASSIGNMENT &&
    !!props.module.lessonAssignmentUuid,
);

const isOverdue = computed(() => {
  const endDateTime = sectionSettings.value?.endDateTime;
  if (!endDateTime) return false;

  const dateTime = DateTimeFormatter.formatLocalDateTimeToDateTime(endDateTime);
  return DateTimeUtils.isNowAfterDate(dateTime);
});

const isBlank = computed(() => {
  const noDescription = sectionDescription.value === t("module_plan_no_description");

  const noNotes = sectionNotes.value === t("module_plan_no_notes");

  return noDescription && (!isTeacher.value || noNotes);
});

const isRestricted = computed(() =>
  sectionAccess.value?.failedReasons.includes(FAILED_REASON.PREREQUISITE_ACCESS_DENIED),
);

const dateFormatter = (date: string) => {
  return DateTimeFormatter.formatLocalDateTimeToDateOnly(date);
};

const timeFormatter = (time: string) => {
  return DateTimeFormatter.formatLocalDateTimeToTimeAmPm(time);
};

const handleMonitor = () => {
  const assignmentUuid = props.module.metaData?.uuid;
  router.push({
    name: props.module.eAssessment
      ? ROUTES_VIEW.ASSESSMENT.MONITORING
      : ROUTES_VIEW.ASSIGNMENT.MONITORING,
    params: { assignmentUuid },
  });
};

const toggleSectionAccordion = (event: { state: boolean[] }) => {
  emit("toggleSectionAccordion", {
    sectionId: props.section.id,
    expanded: event.state[0],
  });
};

const checkSectionIdMatched = (sectionId: string) => {
  const section = props.params.highlights?.sections?.find(
    (section: { id: string }) => section.id === sectionId,
  );
  return !!section; // Returns true if a section is found, false otherwise
};

const handleRadioChange = (sectionId: string) => {
  emit("updateSelectedSection", sectionId);
};

const handleCheckboxChange = (sectionId: string, checked: string[]) => {
  const isChecked = checked.includes(sectionId.toString());
  emit("updateSelectedSection", sectionId, isChecked);
};
</script>

<style></style>
